/**
 * Shared constants for the Prod Soft Launch access restrictions (AB172621).
 *
 * During the Soft Launch window the app temporarily blocks self-service
 * registration and restricts login to approved court domains. When a user is
 * turned away by one of these restrictions we record which restriction fired in
 * `sessionStorage`, so the shared message page can render the matching copy and
 * the message guard can allow that page to activate. The marker is cleared on
 * logout so a later approved-domain sign-in in the same tab starts clean.
 */

/**
 * `sessionStorage` key holding the reason a user was turned away by a Soft
 * Launch restriction. Present only while the user is on the message page after
 * being blocked; removed on logout.
 */
export const SOFT_LAUNCH_BLOCK_REASON_KEY = 'softLaunchBlockReason';

/**
 * Which Soft Launch restriction turned the user away. Persisted as the value of
 * {@link SOFT_LAUNCH_BLOCK_REASON_KEY}.
 */
export enum SoftLaunchBlockReason {
  /** The user's email domain is not on the approved login-domain list. */
  Login = 'login',
  /** Self-service registration is disabled; a registration route was blocked. */
  Registration = 'registration',
}

/**
 * User-facing copy shown on the message page for each Soft Launch block reason.
 * Draft wording — pending PO/UX confirmation (see spec open question 2).
 */
export const SOFT_LAUNCH_MESSAGES: Record<SoftLaunchBlockReason, string> = {
  [SoftLaunchBlockReason.Login]:
    'Sign-in is currently restricted to authorized court personnel. Please contact the First Judicial District for assistance.',
  [SoftLaunchBlockReason.Registration]:
    'Self-service registration is temporarily unavailable. Please check back when the system opens for public use.',
};
